import React, { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import TagUsageModal from "./TagUsageModal";
import styles from "./TagLibrary.module.css";

const TagPanel = ({ service, entityLabel, affectedLabel }) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [modalMode, setModalMode] = useState(null);
  const [modalEntries, setModalEntries] = useState([]);
  const [isFetchingUsage, setIsFetchingUsage] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [isSavingTag, setIsSavingTag] = useState(false);

  const fetchList = () => {
    setIsLoading(true);
    service
      .getList()
      .then((res) => setItems(res || []))
      .catch(() => setItems([]))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchList();
    setSelectedIds([]);
    setSearchValue("");
    setIsCreating(false);
    setNewTagName("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const togglePill = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openModal = (mode) => {
    const targetIds = mode === "rename" ? [selectedIds[0]] : selectedIds;
    setIsFetchingUsage(true);
    Promise.all(targetIds.map((id) => service.getUsage(id)))
      .then((results) => {
        const entries = targetIds.map((id, index) => ({
          item: items.find((i) => i.id === id),
          usage: results[index] || {},
        }));
        setModalEntries(entries);
        setModalMode(mode);
      })
      .finally(() => setIsFetchingUsage(false));
  };

  const closeModal = () => {
    setModalMode(null);
    setModalEntries([]);
  };

  const handleConfirmed = () => {
    closeModal();
    setSelectedIds([]);
    fetchList();
  };

  const startCreating = () => {
    setIsCreating(true);
    setNewTagName("");
  };

  const cancelCreating = () => {
    setIsCreating(false);
    setNewTagName("");
  };

  const handleCreateTag = () => {
    const trimmedName = newTagName.trim();
    if (!trimmedName) return;

    setIsSavingTag(true);
    service
      .create(trimmedName)
      .then((res) => {
        setItems((prev) =>
          prev.some((i) => i.id === res.id) ? prev : [...prev, res]
        );
        setNewTagName("");
      })
      .finally(() => setIsSavingTag(false));
  };

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.searchInputWrapper}>
          <Search className={styles.searchIcon} size={16} />
          <input
            type="text"
            placeholder={`Search ${entityLabel.toLowerCase()}...`}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          {searchValue && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => setSearchValue("")}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {isCreating ? (
          <div className={styles.createRow}>
            <input
              type="text"
              placeholder={`New ${entityLabel.toLowerCase()} name`}
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreateTag();
                }
              }}
              autoFocus
            />
            <button
              type="button"
              className={`${styles.actionButton} ${styles.createButton}`}
              disabled={!newTagName.trim() || isSavingTag}
              onClick={handleCreateTag}
            >
              Create
            </button>
            <button
              type="button"
              className={styles.actionButton}
              onClick={cancelCreating}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className={styles.actionButtons}>
            {selectedIds.length > 0 && (
              <>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.renameButton}`}
                  disabled={selectedIds.length !== 1 || isFetchingUsage}
                  onClick={() => openModal("rename")}
                >
                  Rename
                </button>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.deleteButton}`}
                  disabled={isFetchingUsage}
                  onClick={() => openModal("delete")}
                >
                  Delete
                </button>
              </>
            )}
            <button
              type="button"
              className={`${styles.actionButton} ${styles.createButton}`}
              onClick={startCreating}
            >
              + New {entityLabel}
            </button>
          </div>
        )}
      </div>

      <div className={styles.pillGrid}>
        {isLoading ? (
          <p>Loading...</p>
        ) : filteredItems.length === 0 ? (
          <p>No {entityLabel.toLowerCase()}s found.</p>
        ) : (
          filteredItems.map((item) => (
            <span
              key={item.id}
              className={`pill secondary ${styles.pill} ${
                selectedIds.includes(item.id) ? styles.pillSelected : ""
              }`}
              onClick={() => togglePill(item.id)}
            >
              {item.name}
            </span>
          ))
        )}
      </div>

      {modalMode && (
        <TagUsageModal
          mode={modalMode}
          entries={modalEntries}
          affectedLabel={affectedLabel}
          service={service}
          onCancel={closeModal}
          onConfirmed={handleConfirmed}
        />
      )}
    </div>
  );
};

export default TagPanel;
